/**
 * Integrations Tab - Phase 10c.7: Integration Layer
 *
 * Manage webhook endpoints for Teams, Google Chat, Slack, and custom notifications.
 * - Register webhook URLs with event type subscriptions
 * - Auto-detects platform from URL and sends native-format cards
 * - Test endpoints with a sample event
 * - View delivery log and stats
 */
import { useState } from 'react';
import { Plug, Plus, Trash2, Play, ExternalLink, CheckCircle2, XCircle, Clock, Copy, Eye, EyeOff } from 'lucide-react';
import { QuickBooksSetup } from './QuickBooksSetup';
import { QboTaxCodeMapSettings } from './QboTaxCodeMapSettings';
import { DropboxSetup } from './DropboxSetup';
import {
  useWebhookEndpoints,
  useWebhookEvents,
  useWebhookEventStats,
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useTestWebhookEndpoint,
  useValidateSlackToken,
  useSetupSlackBot,
  WEBHOOK_EVENT_TYPES,
  type WebhookEndpoint,
  type WebhookEvent,
} from '@/hooks/useWebhooks';

// ============================================================================
// Create Endpoint Form
// ============================================================================

function CreateEndpointForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [signingSecret, setSigningSecret] = useState<string | null>(null);

  const createMutation = useCreateWebhookEndpoint();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !url || eventTypes.length === 0) return;

    const result = await createMutation.mutateAsync({ name, url, event_types: eventTypes });
    if (result.signing_secret) {
      setSigningSecret(result.signing_secret);
    }
  };

  const toggleEventType = (type: string) => {
    setEventTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const copySecret = () => {
    if (signingSecret) {
      navigator.clipboard.writeText(signingSecret);
    }
  };

  // Show signing secret after creation
  if (signingSecret) {
    return (
      <div className="border border-green-200 bg-green-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-green-800 font-medium">
          <CheckCircle2 className="w-4 h-4" />
          Endpoint created
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Signing Secret (copy now — won't be shown again)
          </label>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono break-all">
              {signingSecret}
            </code>
            <button
              onClick={copySecret}
              className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded"
              title="Copy"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-4 space-y-4">
      <h3 className="font-medium text-gray-900">Register Webhook Endpoint</h3>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Teams — Deliverables Channel"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
        <input
          type="url"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://chat.googleapis.com/v1/spaces/... or https://hooks.slack.com/..."
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Google Chat, Slack, Teams, or any incoming webhook URL. Platform is auto-detected.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Event Types</label>
        <div className="space-y-2">
          {WEBHOOK_EVENT_TYPES.map(type => (
            <label key={type.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={eventTypes.includes(type.value)}
                onChange={() => toggleEventType(type.value)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{type.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={createMutation.isPending || !name || !url || eventTypes.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {createMutation.isPending ? 'Creating...' : 'Create Endpoint'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// Endpoint Row
// ============================================================================

function EndpointRow({ endpoint }: { endpoint: WebhookEndpoint }) {
  const [showEvents, setShowEvents] = useState(false);
  const deleteMutation = useDeleteWebhookEndpoint();
  const testMutation = useTestWebhookEndpoint();
  const toggleMutation = useUpdateWebhookEndpoint();

  const handleToggle = () => {
    toggleMutation.mutate({ id: endpoint.id, data: { is_active: !endpoint.is_active } });
  };

  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-900 truncate">{endpoint.name}</h4>
              {endpoint.is_active ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  Disabled
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 font-mono truncate mt-1">{endpoint.url}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {endpoint.event_types.map(type => (
                <span
                  key={type}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
                >
                  {type}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1 ml-4">
            <button
              onClick={() => testMutation.mutate(endpoint.id)}
              disabled={testMutation.isPending || !endpoint.is_active}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30"
              title="Send test event"
            >
              <Play className="w-4 h-4" />
            </button>
            <button
              onClick={handleToggle}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title={endpoint.is_active ? 'Disable' : 'Enable'}
            >
              {endpoint.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              onClick={() => {
                if (confirm(`Delete "${endpoint.name}"? This will also delete its delivery log.`)) {
                  deleteMutation.mutate(endpoint.id);
                }
              }}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <button
          onClick={() => setShowEvents(!showEvents)}
          className="text-xs text-blue-600 hover:text-blue-800 mt-2"
        >
          {showEvents ? 'Hide delivery log' : 'Show delivery log'}
        </button>
      </div>

      {showEvents && <EndpointEventLog endpointId={endpoint.id} />}
    </div>
  );
}

// ============================================================================
// Event Log
// ============================================================================

function EndpointEventLog({ endpointId }: { endpointId: string }) {
  const { data: events, isLoading } = useWebhookEvents(endpointId, 20);

  if (isLoading) return <div className="px-4 pb-4 text-sm text-gray-500">Loading...</div>;

  if (!events || events.length === 0) {
    return (
      <div className="px-4 pb-4 text-sm text-gray-500 border-t border-gray-100 pt-3">
        No events dispatched yet. Use the test button to send a sample event.
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
            <th className="px-4 py-2">Event</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">HTTP</th>
            <th className="px-4 py-2">Time</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event: WebhookEvent) => (
            <tr key={event.id} className="border-b border-gray-50 last:border-0">
              <td className="px-4 py-2 text-gray-700">{event.event_type}</td>
              <td className="px-4 py-2">
                {event.status === 'success' && (
                  <span className="inline-flex items-center gap-1 text-green-700">
                    <CheckCircle2 className="w-3 h-3" /> Success
                  </span>
                )}
                {event.status === 'failed' && (
                  <span className="inline-flex items-center gap-1 text-red-700" title={event.error_message || ''}>
                    <XCircle className="w-3 h-3" /> Failed
                  </span>
                )}
                {event.status === 'pending' && (
                  <span className="inline-flex items-center gap-1 text-yellow-700">
                    <Clock className="w-3 h-3" /> Pending
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-gray-500 font-mono">
                {event.response_status || '—'}
              </td>
              <td className="px-4 py-2 text-gray-500">
                {new Date(event.created_at).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Slack Bot Setup Form (Phase 10c.10)
// ============================================================================

function SlackBotSetupForm({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<'token' | 'channel' | 'done'>('token');
  const [botToken, setBotToken] = useState('');
  const [name, setName] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [channelMode, setChannelMode] = useState<'single' | 'per_project'>('per_project');
  const [eventTypes, setEventTypes] = useState<string[]>(
    WEBHOOK_EVENT_TYPES.map(t => t.value)
  );
  const [channels, setChannels] = useState<{ id: string; name: string; num_members: number }[]>([]);
  const [botInfo, setBotInfo] = useState<{ team?: string } | null>(null);

  const validateMutation = useValidateSlackToken();
  const setupMutation = useSetupSlackBot();

  const handleValidate = async () => {
    if (!botToken.startsWith('xoxb-')) return;
    const result = await validateMutation.mutateAsync(botToken);
    if (result.valid && result.channels) {
      setChannels(result.channels);
      setBotInfo(result.bot_info);
      setStep('channel');
    }
  };

  const handleSetup = async () => {
    if (!name || !selectedChannel || eventTypes.length === 0) return;
    await setupMutation.mutateAsync({
      name,
      bot_token: botToken,
      channel_id: selectedChannel,
      event_types: eventTypes,
      channel_mode: channelMode,
    });
    setStep('done');
  };

  const toggleEventType = (type: string) => {
    setEventTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  if (step === 'done') {
    return (
      <div className="border border-green-200 bg-green-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-green-800 font-medium">
          <CheckCircle2 className="w-4 h-4" />
          Slack Bot connected
        </div>
        <p className="text-sm text-green-700">
          Notifications will now appear in your Slack channel with threading and @mentions.
        </p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
        >
          Done
        </button>
      </div>
    );
  }

  if (step === 'channel') {
    return (
      <div className="border border-gray-200 rounded-lg p-4 space-y-4">
        <h3 className="font-medium text-gray-900">Connect Slack Bot</h3>
        <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
          Token valid — {botInfo?.team || 'workspace'} connected
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Slack — Project Updates"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Channel Mode</label>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
              style={{ backgroundColor: channelMode === 'per_project' ? '#EFF6FF' : undefined, borderColor: channelMode === 'per_project' ? '#3B82F6' : undefined }}
            >
              <input
                type="radio"
                name="channelMode"
                value="per_project"
                checked={channelMode === 'per_project'}
                onChange={() => setChannelMode('per_project')}
                className="mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Channel per project</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  Auto-creates a Slack channel for each project (e.g. #proj-main-street).
                  Team members join only the projects they work on.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
              style={{ backgroundColor: channelMode === 'single' ? '#EFF6FF' : undefined, borderColor: channelMode === 'single' ? '#3B82F6' : undefined }}
            >
              <input
                type="radio"
                name="channelMode"
                value="single"
                checked={channelMode === 'single'}
                onChange={() => setChannelMode('single')}
                className="mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Single channel with threads</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  All notifications go to one channel, organized by project threads.
                </p>
              </div>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {channelMode === 'per_project' ? 'Fallback Channel' : 'Channel'}
          </label>
          <select
            value={selectedChannel}
            onChange={e => setSelectedChannel(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a channel...</option>
            {channels.map(ch => (
              <option key={ch.id} value={ch.id}>
                #{ch.name} ({ch.num_members} members)
              </option>
            ))}
          </select>
          {channelMode === 'per_project' && (
            <p className="text-xs text-gray-500 mt-1">
              Used for non-project events (e.g. invoices without a project). Project channels are created automatically.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Event Types</label>
          <div className="space-y-2">
            {WEBHOOK_EVENT_TYPES.map(type => (
              <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={eventTypes.includes(type.value)}
                  onChange={() => toggleEventType(type.value)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSetup}
            disabled={setupMutation.isPending || !name || !selectedChannel || eventTypes.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {setupMutation.isPending ? 'Connecting...' : 'Connect Slack Bot'}
          </button>
          <button
            type="button"
            onClick={() => setStep('token')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Step: token
  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <h3 className="font-medium text-gray-900">Connect Slack Bot</h3>
      <p className="text-sm text-gray-600">
        Enter your Slack Bot token to enable rich notifications with threading and @mentions.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bot Token</label>
        <input
          type="password"
          value={botToken}
          onChange={e => setBotToken(e.target.value)}
          placeholder="xoxb-..."
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          From your Slack app's OAuth & Permissions page. Requires: chat:write, channels:read, users:read.email
        </p>
      </div>

      {validateMutation.data && !validateMutation.data.valid && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
          Invalid token: {validateMutation.data.error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={handleValidate}
          disabled={validateMutation.isPending || !botToken.startsWith('xoxb-')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {validateMutation.isPending ? 'Validating...' : 'Validate & Continue'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Stats Bar
// ============================================================================

function StatsBar() {
  const { data: stats } = useWebhookEventStats();

  if (!stats || stats.total === 0) return null;

  return (
    <div className="flex items-center gap-4 text-sm">
      <span className="text-gray-500">Last 24h:</span>
      <span className="text-green-700">{stats.success} delivered</span>
      {stats.failed > 0 && <span className="text-red-700">{stats.failed} failed</span>}
      {stats.pending > 0 && <span className="text-yellow-700">{stats.pending} pending</span>}
    </div>
  );
}

// ============================================================================
// Main Tab Component
// ============================================================================

export function IntegrationsTab() {
  const [showCreate, setShowCreate] = useState(false);
  const [showSlackSetup, setShowSlackSetup] = useState(false);
  const { data: endpoints, isLoading } = useWebhookEndpoints(true);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Plug className="w-5 h-5 text-blue-600" />
            Integrations
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Connect Keystone to Google Chat, Slack, Microsoft Teams, or any webhook endpoint.
            Status changes, new deliverables, and invoice events trigger real-time notifications
            with native rich cards for each platform.
          </p>
        </div>
        <StatsBar />
      </div>

      {/* QuickBooks Online */}
      <QuickBooksSetup />

      {/* QBO CSV export tax-code map (R2 — SPEC-qbo-csv-harden) */}
      <div className="border-t border-gray-200 pt-6">
        <QboTaxCodeMapSettings />
      </div>

      {/* Dropbox */}
      <DropboxSetup />

      {/* Webhooks Section Header */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Webhook Notifications</h3>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 space-y-4">
        <p className="font-medium text-base">Setup Guide</p>
        <p className="text-blue-700">
          Paste any incoming webhook URL below. Keystone auto-detects the platform and sends
          native rich cards (Google Chat Cards, Slack Blocks, or Teams Adaptive Cards).
        </p>

        <div className="space-y-3">
          <div>
            <p className="font-medium">Google Chat</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700 ml-1">
              <li>Open Google Chat &gt; find or create a Space</li>
              <li>Click the space name &gt; Apps & integrations &gt; Manage webhooks</li>
              <li>Click "Add webhook", name it "Keystone", and copy the URL</li>
            </ol>
          </div>

          <hr className="border-blue-200" />

          <div>
            <p className="font-medium">Slack</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700 ml-1">
              <li>Go to <code className="bg-blue-100 px-1 rounded">api.slack.com/apps</code> &gt; Create New App &gt; From scratch</li>
              <li>Enable Incoming Webhooks under Features</li>
              <li>Click "Add New Webhook to Workspace" and pick a channel</li>
              <li>Copy the webhook URL (starts with hooks.slack.com)</li>
            </ol>
          </div>

          <hr className="border-blue-200" />

          <div>
            <p className="font-medium">Microsoft Teams</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700 ml-1">
              <li>In Teams, go to your channel &gt; Manage channel &gt; Connectors or Workflows</li>
              <li>Add an "Incoming Webhook" and copy the URL</li>
            </ol>
            <p className="text-blue-600 text-xs mt-1">Requires Teams with Microsoft 365 (paid). For free alternatives, use Google Chat or Slack above.</p>
          </div>

          <hr className="border-blue-200" />

          <div>
            <p className="font-medium">Custom / Power Automate</p>
            <p className="text-blue-700">
              Any other URL receives an HMAC-signed JSON payload with an <code className="bg-blue-100 px-1 rounded">adaptive_card</code> field
              for downstream routing.
            </p>
          </div>
        </div>
      </div>

      {/* Create forms or buttons */}
      {showSlackSetup ? (
        <SlackBotSetupForm onClose={() => setShowSlackSetup(false)} />
      ) : showCreate ? (
        <CreateEndpointForm onClose={() => setShowCreate(false)} />
      ) : (
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSlackSetup(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#4A154B] text-white rounded-md text-sm font-medium hover:bg-[#3e1140]"
          >
            <Plus className="w-4 h-4" />
            Connect Slack Bot
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Plus className="w-4 h-4" />
            Add Webhook Endpoint
          </button>
        </div>
      )}

      {/* Endpoints list */}
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading endpoints...</div>
      ) : !endpoints || endpoints.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Plug className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p>No webhook endpoints registered yet.</p>
          <p className="text-sm mt-1">Add one to start receiving notifications in Google Chat, Slack, or Teams.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {endpoints.map(endpoint => (
            <EndpointRow key={endpoint.id} endpoint={endpoint} />
          ))}
        </div>
      )}
    </div>
  );
}
